import React, { useContext, useEffect, useState } from "react";

import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { useTranslation } from "react-i18next";
import { CREATE_CUSTOMER } from "shared/graphql/mutations";
import { CIVILITIES, COUNTRIES, CUSTOMER_INFO } from "shared/graphql/queries";
import { StoreContext } from "shared/store";

import Notification from "../../../shared/components/Notification";
import { NAME_SPACES, DEFAULT_VALUES } from "../../../shared/locals/constants";
import { useInfo } from "../../../utils/hooks/info";

import { NOTIFICATION_ACCESSORS } from "./constants";
import View from "./View";

const Component = () => {
  const [loading, setLoading] = useState(false);
  const {
    email,
    setCustomerInfo,
    customerInfo,
    commonInfo: { knownCustomer },
  } = useContext(StoreContext);
  const { getCommonInfo } = useInfo();
  const civilitiesQuery = useQuery(CIVILITIES);
  const countriesQuery = useQuery(COUNTRIES);
  const [values, setValues] = useState({
    civilityCode: customerInfo.civilityCode,
    firstname: customerInfo.firstname,
    name: customerInfo.name,
    address1: customerInfo.address1,
    address2: customerInfo.address2,
    town: customerInfo.town,
    postCode: customerInfo.postCode,
    countryCode: customerInfo.countryCode,
    email: customerInfo.email || email,
    cellPhone: customerInfo.cellPhone,
    languageCode: customerInfo.languageCode,
    geolocalized: customerInfo.geolocalized,
    dataList: customerInfo.dataList || [],
  });

  const [getCustomer, query] = useLazyQuery(CUSTOMER_INFO, {
    fetchPolicy: "no-cache",
    onCompleted: (result) => {
      setCustomerInfo(get(result, "customerInfo", {}));
    },
    onError: (error) => {
      const _error = get(error.graphQLErrors, "[0].extensions");
      setCustomerError(_error);
    },
  });
  const [createCustomer] = useMutation(CREATE_CUSTOMER, {
    onCompleted: () => {
      query.refetch();
    },
    onError: () => {
      setLoading(false);
    },
  });
  const [customerError, setCustomerError] = useState();
  const { t: i18n } = useTranslation(NAME_SPACES.ACCOUNT);

  useEffect(() => {
    const dataList = [...(customerInfo.dataList || [])].map(
      // eslint-disable-next-line no-unused-vars
      ({ __typename, ...item }) => item,
    );
    const smsData = dataList.find(
      ({ name }) => name === NOTIFICATION_ACCESSORS.SMS,
    );
    const emailData = dataList.find(
      ({ name }) => name === NOTIFICATION_ACCESSORS.EMAIL,
    );
    isEmpty(smsData) &&
      dataList.push({ name: NOTIFICATION_ACCESSORS.SMS, value: "0" });
    isEmpty(emailData) &&
      dataList.push({ name: NOTIFICATION_ACCESSORS.EMAIL, value: "0" });
    setValues({
      civilityCode: customerInfo.civilityCode,
      firstname: customerInfo.firstname,
      name: customerInfo.name,
      address1: customerInfo.address1,
      address2: customerInfo.address2,
      town: customerInfo.town,
      postCode: customerInfo.postCode,
      countryCode: customerInfo.countryCode,
      email: customerInfo.email || email,
      cellPhone: customerInfo.cellPhone,
      languageCode: customerInfo.languageCode,
      geolocalized: customerInfo.geolocalized,
      dataList,
    });
  }, [customerInfo]);

  useEffect(() => {
    knownCustomer && getCustomer();
  }, [knownCustomer]);

  const onSubmit = async () => {
    setLoading(true);
    const data = Object.entries(values).reduce(
      (a, [k, v]) => (v == null ? a : ((a[k] = v), a)),
      {},
    );
    await createCustomer({
      variables: { data },
    });
    getCommonInfo();
    setLoading(false);
    Notification.info({
      duration: DEFAULT_VALUES.NOTIFICATION_DURATION,
      message: i18n("CHANGES_WERE_SAVED"),
    });
  };

  return (
    <View
      loading={loading}
      values={values}
      error={customerError}
      onChange={setValues}
      civilitiesQuery={{
        loading: civilitiesQuery.loading,
        data: get(civilitiesQuery, "data.civilities.data", []),
      }}
      countriesQuery={{
        loading: countriesQuery.loading,
        data: get(countriesQuery, "data.countries.data", []),
      }}
      onSubmit={onSubmit}
    />
  );
};

export default Component;
